import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Badge,
    Popover,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import transactionAPI from '../../Network/Transaction';
import moment from 'moment';

const ProgressNotifications = () => {
    const [recentProcesses, setRecentProcesses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Popover state
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'progress-popover' : undefined;

    // Per-record detail dialog state
    const [detailProcess, setDetailProcess] = useState(null);
    const [detailRecords, setDetailRecords] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const detailOpen = Boolean(detailProcess);

    const fetchProcesses = useCallback(async () => {
        try {
            const response = await transactionAPI.getRecentProcesses();
            if (response?.data?.status && response?.data?.data) {
                setRecentProcesses(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching recent processes:", error);
        }
    }, []);

    // Poll the backend periodically every 2 seconds
    useEffect(() => {
        // Initial fetch
        fetchProcesses();

        const intervalId = setInterval(fetchProcesses, 3000);
        
        return () => clearInterval(intervalId);
    }, [fetchProcesses]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        fetchProcesses(); // Fetch immediately when opening
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatProcessName = (name) => {
        if (name === 'reprocess_missing_units') {
            return "معالجة المستندات بدون مركبة";
        }
        if (name === 'mizan_units_sync') {
            return "مزامنة مركبات الميزان";
        }
        return name;
    }

    // Open the per-record detail dialog for a process. Fetches /processes/:id/records
    // each time so the list reflects the latest data even if the user opens a
    // running process and re-opens it after more records have been logged.
    const openProcessDetail = async (process) => {
        setDetailProcess(process);
        setDetailRecords([]);
        setDetailLoading(true);
        try {
            const res = await transactionAPI.getProcessRecords({ processId: process.id });
            if (res?.data?.status && Array.isArray(res?.data?.data)) {
                setDetailRecords(res.data.data);
            } else {
                setDetailRecords([]);
            }
        } catch (err) {
            console.error('Error loading process records:', err);
            setDetailRecords([]);
        }
        setDetailLoading(false);
    };

    const closeProcessDetail = () => {
        setDetailProcess(null);
        setDetailRecords([]);
    };

    const runningCount = recentProcesses.filter(p => p.status === 'running').length;

    return (
        <>
            <IconButton 
                color="inherit" 
                onClick={handleClick}
                sx={{ 
                    ml: 1, 
                    mr: 1,
                    backgroundColor: open ? 'rgba(0,0,0,0.05)' : 'transparent'
                }}
            >
                <Badge badgeContent={runningCount} color="error">
                    <NotificationsIcon sx={{ color: '#FFFFFF' }} />
                </Badge>
            </IconButton>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: '350px',
                        maxHeight: '400px',
                        mt: 1,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontFamily: 'Cairo-Bold' }}>
                       الاشعارات
                    </Typography>
                    {runningCount > 0 && <CircularProgress size={16} />}
                </Box>
                
                <List sx={{ p: 0, maxHeight: '330px', overflowY: 'auto' }}>
                    {recentProcesses.length === 0 ? (
                        <ListItem sx={{ p: 3, justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Cairo-Medium' }}>
                                لا توجد عمليات حالية
                            </Typography>
                        </ListItem>
                    ) : (
                        recentProcesses.map((process, index) => {
                            const progress = process.total_records > 0 
                                ? Math.round((process.total_processed / process.total_records) * 100) 
                                : 0;
                            
                            const isRunning = process.status === 'running';
                            const isCompleted = process.status === 'completed';
                            const isFailed = process.status === 'failed';

                            return (
                                <React.Fragment key={process.id}>
                                    <ListItem
                                        button
                                        onClick={() => openProcessDetail(process)}
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            p: 2,
                                            direction: 'rtl',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                {isRunning && <AutorenewIcon sx={{ animation: "spin 2s linear infinite", color: '#1976d2' }} />}
                                                {isCompleted && <CheckCircleOutlineIcon color="success" />}
                                                {isFailed && <ErrorOutlineIcon color="error" />}
                                            </ListItemIcon>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontFamily: 'Cairo-Bold', lineHeight: 1.2 }}>
                                                    {formatProcessName(process.name)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Cairo-Medium' }}>
                                                    {moment(process.updated_at).fromNow()}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', ml: 1, color: isRunning ? 'primary.main' : 'text.secondary' }}>
                                                {isRunning ? `${progress}%` : (isCompleted ? 'مكتمل' : 'فشل')}
                                            </Typography>
                                        </Box>
                                        
                                        {isRunning && (
                                            <Box sx={{ width: '100%', mb: 1 }}>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={progress} 
                                                    sx={{ height: 6, borderRadius: 3 }} 
                                                />
                                            </Box>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Cairo-Medium' }}>
                                                الإجمالي: {process.total_records}
                                            </Typography>
                                            <Typography variant="caption" color="success.main" sx={{ fontFamily: 'Cairo-Medium' }}>
                                                نجاح: {process.total_success}
                                            </Typography>
                                            <Typography variant="caption" color="error.main" sx={{ fontFamily: 'Cairo-Medium' }}>
                                                فشل: {process.total_failed}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                    {index < recentProcesses.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })
                    )}
                </List>
            </Popover>

            <Dialog
                open={detailOpen}
                onClose={closeProcessDetail}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { direction: 'rtl' } }}
            >
                <DialogTitle sx={{ fontFamily: 'Cairo-Bold', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontFamily: 'Cairo-Bold' }}>
                            {detailProcess ? `تفاصيل ${formatProcessName(detailProcess.name)}` : ''}
                        </Typography>
                        {detailProcess && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Cairo-Medium' }}>
                                {moment(detailProcess.updated_at).format('YYYY-MM-DD HH:mm:ss')} — الإجمالي: {detailProcess.total_records} | نجاح: {detailProcess.total_success} | فشل: {detailProcess.total_failed}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={closeProcessDetail} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {detailLoading ? (
                        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : detailRecords.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Cairo-Medium' }}>
                                لا توجد تفاصيل مسجلة لهذه العملية
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {detailRecords.map((rec, idx) => {
                                const isSuccess = rec.status === 'success';
                                const isSkipped = rec.status === 'skipped';
                                const chipColor = isSuccess ? 'success' : (isSkipped ? 'warning' : 'error');
                                const chipLabel = isSuccess ? 'نجاح' : (isSkipped ? 'تم التجاوز' : 'فشل');
                                return (
                                    <React.Fragment key={rec.id}>
                                        <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.25, px: 2, direction: 'rtl' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, mb: 0.5 }}>
                                                <Chip size="small" color={chipColor} label={chipLabel} sx={{ fontFamily: 'Cairo-Bold' }} />
                                                <Typography variant="subtitle2" sx={{ fontFamily: 'Cairo-Bold' }}>
                                                    {rec.record_ref || '—'}
                                                </Typography>
                                                <Box sx={{ flex: 1 }} />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Cairo-Medium' }}>
                                                    {moment(rec.created_at).format('HH:mm:ss')}
                                                </Typography>
                                            </Box>
                                            {rec.message && (
                                                <Typography variant="body2" sx={{ fontFamily: 'Cairo-Medium', color: '#444', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {rec.message}
                                                </Typography>
                                            )}
                                        </ListItem>
                                        {idx < detailRecords.length - 1 && <Divider />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeProcessDetail} sx={{ fontFamily: 'Cairo-Bold' }}>إغلاق</Button>
                </DialogActions>
            </Dialog>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default ProgressNotifications;
